import React from 'react';
import styled from 'styled-components';
import PageMetaLoader from '../components/PageMetaLoader';
import CtaBanner from '../components/CtaBanner';
import { useI18n } from '../i18n';
import { en, km } from './FeaturesPage.data';

/**
 * Feature reference page written for business audiences: owners, managers,
 * finance, HR, sales, and marketing. Detail is sourced from the AKAS user
 * guides at docs.akas.co, but framed as business outcomes.
 */


const FeaturesPage: React.FC = () => {
  const { locale } = useI18n();
  const t = locale === 'km' ? km : en;
  const groups = t.groups;
  return (
    <PageWrap>
      <PageMetaLoader pageUrl="features" />
      <div className="container">
        <PageHeader>
          <HeroKicker>{t.heroKicker}</HeroKicker>
          <Title>{t.title}</Title>
          <Lead>{t.lead}</Lead>
          <JumpNav>
            {groups.map((group) => (
              <a key={group.id} href={`#${group.id}`}>
                {group.title}
              </a>
            ))}
          </JumpNav>
        </PageHeader>

        {groups.map((group, index) => (
          <GroupSection key={group.id} id={group.id}>
            <GroupHeader>
              <GroupHeading>
                <GroupIndex>{String(index + 1).padStart(2, '0')}</GroupIndex>
                <div>
                  <Kicker>{group.kicker}</Kicker>
                  <GroupTitle>{group.title}</GroupTitle>
                </div>
              </GroupHeading>
              <GroupBadges>
                {group.docsUrl && (
                  <DocsLink href={group.docsUrl} target="_blank" rel="noopener noreferrer">
                    {t.userGuide}
                  </DocsLink>
                )}
              </GroupBadges>
            </GroupHeader>
            <GroupLead>{group.description}</GroupLead>
            <ItemsGrid>
              {group.items.map((item) => (
                <ItemCard key={item.title}>
                  <Icon>{item.icon}</Icon>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                </ItemCard>
              ))}
            </ItemsGrid>
          </GroupSection>
        ))}

        <CtaBanner
          title={t.cta.title}
          description={t.cta.description}
        />
      </div>
    </PageWrap>
  );
};

const PageWrap = styled.div`
  padding: 2.5rem 0 5rem;
  background:
    radial-gradient(900px 420px at 90% 4%, #eef8fd 0%, transparent 60%),
    white;

  .container {
    max-width: 1200px;
  }
`;

const PageHeader = styled.header`
  position: relative;
  overflow: hidden;
  max-width: 1200px;
  margin: 0 auto 1.5rem;
  padding: 3.5rem 2.5rem 3rem;
  border-radius: 28px;
  text-align: center;
  color: white;
  background:
    radial-gradient(700px 320px at 85% -10%, rgba(0, 166, 232, 0.35) 0%, transparent 62%),
    radial-gradient(600px 300px at 0% 100%, rgba(76, 201, 240, 0.18) 0%, transparent 60%),
    var(--gradient-ink);
  box-shadow: var(--shadow-lg);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.14) 1px, transparent 1px);
    background-size: 24px 24px;
    mask-image: radial-gradient(circle at 50% 0%, black 0%, transparent 70%);
    -webkit-mask-image: radial-gradient(circle at 50% 0%, black 0%, transparent 70%);
    opacity: 0.5;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 640px) {
    padding: 2.4rem 1.25rem 2.2rem;
    border-radius: 20px;
  }
`;

const HeroKicker = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.9rem;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.08);
  color: #a8e2f7;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Kicker = styled.p`
  margin: 0 0 0.45rem;
  color: var(--primary-color);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  color: white;
  letter-spacing: -0.04em;
  margin-bottom: 0.9rem;
  line-height: 1.1;
`;

const Lead = styled.p`
  max-width: 720px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  line-height: 1.72;
`;

const JumpNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;

  a {
    display: inline-block;
    padding: 0.45rem 0.95rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.84rem;
    font-weight: 650;
    transition: background 0.22s ease, border-color 0.22s ease, transform 0.22s var(--ease);

    &:hover {
      background: rgba(255, 255, 255, 0.18);
      border-color: rgba(255, 255, 255, 0.42);
      transform: translateY(-1px);
      text-decoration: none;
    }
  }
`;

const GroupSection = styled.section`
  padding-top: 3.6rem;
  scroll-margin-top: 6rem;

  & + & {
    border-top: 1px solid #edf4f8;
    margin-top: 3.6rem;
    padding-top: 3.6rem;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.7rem;
  }
`;

const GroupHeading = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 640px) {
    gap: 0.8rem;
  }
`;

const GroupIndex = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 12px;
  background: var(--gradient-brand);
  color: white;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  box-shadow: 0 8px 18px rgba(0, 142, 204, 0.28);
  margin-top: 0.1rem;
`;

const GroupTitle = styled.h2`
  font-size: clamp(1.5rem, 2.6vw, 2.05rem);
  color: var(--text-dark);
  letter-spacing: -0.03em;
  line-height: 1.2;
  margin: 0;
`;

const GroupBadges = styled.div`
  flex-shrink: 0;
  padding-top: 0.2rem;
`;

const DocsLink = styled.a`
  display: inline-block;
  padding: 0.45rem 1rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: white;
  color: var(--primary-color);
  font-size: 0.84rem;
  font-weight: 700;
  box-shadow: var(--shadow-xs);
  transition: border-color 0.22s ease, background 0.22s ease, transform 0.22s var(--ease);

  &:hover {
    border-color: rgba(0, 142, 204, 0.45);
    background: var(--primary-light);
    transform: translateY(-1px);
    text-decoration: none;
  }
`;

const GroupLead = styled.p`
  max-width: 780px;
  color: var(--text-muted);
  font-size: 1.05rem;
  line-height: 1.72;
  margin: 0.85rem 0 1.7rem;
  padding-left: 3.6rem;

  @media (max-width: 640px) {
    padding-left: 0;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ItemCard = styled.article`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: white;
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 1.25rem 1.3rem;
  box-shadow: var(--shadow-xs);
  transition: transform 0.3s var(--ease), box-shadow 0.3s var(--ease),
    border-color 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 142, 204, 0.3);
    box-shadow: var(--shadow-md);
  }

  h3 {
    font-size: 1.06rem;
    letter-spacing: -0.01em;
    margin: 0 0 0.4rem;
    color: var(--text-dark);
  }

  p {
    margin: 0;
    color: var(--text-muted);
    font-size: 0.94rem;
    line-height: 1.65;
  }
`;

const Icon = styled.span`
  flex-shrink: 0;
  width: 2.6rem;
  height: 2.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #e9f7fd 0%, #d5edf9 100%);
  border: 1px solid rgba(0, 142, 204, 0.12);
  border-radius: 14px;
  font-size: 1.2rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
`;

export default FeaturesPage;
